<script lang="ts">
import type { InputEmits } from '#ui/types';
import type { InputProps } from './Input.vue';
import { useForwardPropsEmits } from 'reka-ui';

export interface InputPasswordProps extends InputProps {
	showIcon?: string;
	hideIcon?: string;
}
</script>

<script setup lang="ts">
import { useId } from 'vue';
import { useI18n } from 'vue-i18n';

const {
	showIcon = 'lucide:eye',
	hideIcon = 'lucide:eye-off',
	id,
	class: className,
	modelValue,
	...forwardedProps
} = defineProps<InputPasswordProps>();
const emits = defineEmits<InputEmits>();
const forwarded = useForwardPropsEmits(() => ({ ...forwardedProps, id, class: className }), emits);

const password = defineModel<string>();
const show = defineModel<boolean>('show');
const { t } = useI18n();
const inputId = id ?? useId();

function toggleShow() {
	show.value = !show.value;
}
</script>

<template>
	<DInput
		v-bind="forwarded"
		:id="inputId"
		v-model="password"
		:model-modifiers="modelModifiers ?? {}"
		:type="show ? 'text' : 'password'"
		:class="className"
		:ui="{ trailing: 'pe-1' }"
	>
		<template #trailing>
			<DButton
				color="neutral"
				variant="link"
				size="md"
				class="text-dimmed"
				:icon="show ? hideIcon : showIcon"
				:aria-label="show ? t('common.password_hide') : t('common.password_show')"
				:aria-pressed="show"
				:aria-controls="inputId"
				@click="toggleShow"
			/>
		</template>
	</DInput>
</template>
